import api from './api.service';

export interface Incident {
  id: string;
  title: string;
  description?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'investigating' | 'identified' | 'monitoring' | 'resolved' | 'closed';
  started_at: string;
  resolved_at?: string;
  duration_seconds?: number;
  affected_services?: string[];
  affected_users?: number;
  impact_level: 'Low' | 'Medium' | 'High' | 'Critical';
  root_cause_analysis_status: 'pending' | 'analyzing' | 'completed';
  root_causes?: RootCause[];
  events?: IncidentEvent[];
}

export interface RootCause {
  id: string;
  incident_id: string;
  cause_description: string;
  confidence_score: number;
  category?: 'infrastructure' | 'application' | 'database' | 'network' | 'external' | 'configuration' | 'code';
  affected_components?: string[];
  correlated_events?: string[];
  recommendation?: string;
  impact_level: 'Low' | 'Medium' | 'High';
  verified: boolean;
}

export interface IncidentEvent {
  id: string;
  incident_id: string;
  event_type: 'symptom' | 'correlated' | 'cause' | 'remediation' | 'status_change' | 'comment';
  event_description: string;
  event_data?: any;
  occurred_at: string;
}

export interface Anomaly {
  id: string;
  anomaly_type: 'metric' | 'trace' | 'log' | 'infrastructure';
  metric_name?: string;
  detected_at: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  anomaly_score?: number;
  expected_value?: number;
  actual_value?: number;
  deviation_percent?: number;
  status: 'new' | 'investigating' | 'resolved' | 'false_positive';
}

export interface ForecastData {
  time: string;
  actual: number | null;
  predicted: number | null;
  upperBound: number | null;
  lowerBound: number | null;
  isPrediction: boolean;
}

export const mlApi = {
  // Incidents
  getIncidents: (status?: string, applicationId?: string | null): Promise<Incident[]> => {
    const params: any = {};
    if (status) params.status = status;
    if (applicationId) params.applicationId = applicationId;
    return api.get('/v1/ml/incidents', { params });
  },

  getIncident: (id: string): Promise<Incident> => {
    return api.get(`/v1/ml/incidents/${id}`);
  },

  createIncident: (data: Partial<Incident>): Promise<Incident> => {
    return api.post('/v1/ml/incidents', data);
  },

  updateIncident: (id: string, data: Partial<Incident>): Promise<Incident> => {
    return api.put(`/v1/ml/incidents/${id}`, data);
  },

  // Root Causes
  getRootCauses: (incidentId: string): Promise<RootCause[]> => {
    return api.get(`/v1/ml/incidents/${incidentId}/root-causes`);
  },

  addRootCause: (incidentId: string, data: Partial<RootCause>): Promise<RootCause> => {
    return api.post(`/v1/ml/incidents/${incidentId}/root-causes`, data);
  },

  // Incident Timeline
  getIncidentTimeline: (incidentId: string): Promise<IncidentEvent[]> => {
    return api.get(`/v1/ml/incidents/${incidentId}/timeline`);
  },

  addIncidentEvent: (incidentId: string, data: Partial<IncidentEvent>): Promise<IncidentEvent> => {
    return api.post(`/v1/ml/incidents/${incidentId}/events`, data);
  },

  // Anomalies
  getAnomalies: (limit: number = 50, applicationId?: string | null): Promise<Anomaly[]> => {
    const params: any = { limit };
    if (applicationId) params.applicationId = applicationId;
    return api.get('/v1/ml/anomalies', { params });
  },

  updateAnomalyStatus: (
    anomalyId: string,
    status: 'new' | 'investigating' | 'resolved' | 'false_positive',
    notes?: string
  ): Promise<Anomaly> => {
    return api.put(`/v1/ml/anomalies/${anomalyId}`, { status, notes });
  },

  // Forecasts
  getForecasts: (metricName: string, startTime: number, endTime: number, applicationId?: string | null): Promise<ForecastData[]> => {
    const params: any = {
      metricName,
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString()
    };
    if (applicationId) params.applicationId = applicationId;
    return api.get('/v1/ml/forecasts', { params });
  },

  // Analyze anomaly root causes
  analyzeAnomalyRootCauses: (anomalyId: string): Promise<any> => {
    return api.get(`/v1/ml/anomalies/${anomalyId}/root-causes`);
  },

  // Get metric correlations
  getMetricCorrelations: (applicationId?: string | null): Promise<{ metric: string; value: number; color: string }[]> => {
    const params: any = {};
    if (applicationId) params.applicationId = applicationId;
    return api.get('/v1/ml/metric-correlations', { params });
  },
};
